import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Flex, 
  Text, 
  Button, 
  TextField, 
  Select, 
  Card, 
  Badge, 
  Dialog, 
  Switch,
  Separator,
  Heading
} from '@radix-ui/themes';
import * as Tabs from '@radix-ui/react-tabs';
import { MagnifyingGlassIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { getApiUrl, API_ENDPOINTS, getAuthHeaders } from '../../../config/api.config';
import { PDFExportButton } from '../../../components/PDFExport';

interface StudentSubmission {
  id: string;
  classId: {
    id: string;
    name: string;
  };
  totalScore: number;
  submittedAt: string;
  status: string;
  submissions: Array<{
    questionId: {
      id: string;
      question: string;
      marks: number;
    };
    answer: string;
    score: number;
    attempts: number;
    submittedAt: string;
    contentModeration?: {
      isInappropriate: boolean;
      reason: string;
      analysis: {
        isGenuineAttempt: boolean;
        containsProfanity: boolean;
      };
    };
    feedback?: {
      aiComment: string;
      suggestion: string;
      markRange: string;
      gradedAt: string | null;
    };
  }>;
}

const StudentSubmissions: React.FC = () => {
  const [submissions, setSubmissions] = useState<StudentSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<StudentSubmission | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [sortBy, setSortBy] = useState('recent');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const navigate = useNavigate();

  const fetchSubmissions = useCallback(async () => {
    try {
      const response = await axios.get(
        getApiUrl(API_ENDPOINTS.SUBMISSION.LIST),
        { headers: getAuthHeaders() }
      );
      
      // Handle both response formats like the original MySubmissions.jsx
      let submissionsData: StudentSubmission[] = [];
      if (response.data && Array.isArray(response.data)) {
        submissionsData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        submissionsData = response.data.data;
      } else {
        console.error('Unexpected response format:', response.data);
        setError('Failed to load submissions - unexpected data format');
        setLoading(false);
        return;
      }

      setSubmissions(submissionsData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching submissions:', err);
      setError('Failed to load submissions');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleViewSubmission = (submission: StudentSubmission) => {
    setSelectedSubmission(submission);
    setShowDetailsModal(true);
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'No date available';
    
    try {
      const date = new Date(dateString);
      const dateStr = date.toLocaleDateString('en-GB');
      const timeStr = date.toLocaleTimeString('en-GB');
      return `${dateStr}, ${timeStr}`;
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getSortedAndFilteredSubmissions = () => {
    let filteredSubmissions = [...submissions];

    // Apply flag filter
    if (showFlaggedOnly) {
      filteredSubmissions = filteredSubmissions.filter(submission => {
        return submission.submissions?.some(sub => 
          sub.contentModeration?.isInappropriate === true
        ) || false;
      });
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredSubmissions = filteredSubmissions.filter(submission => 
        submission.classId?.name?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    switch (sortBy) {
      case 'recent':
        return filteredSubmissions.sort((a, b) => 
          new Date(b.submittedAt || 0).getTime() - new Date(a.submittedAt || 0).getTime()
        );
      case 'oldest':
        return filteredSubmissions.sort((a, b) => 
          new Date(a.submittedAt || 0).getTime() - new Date(b.submittedAt || 0).getTime()
        );
      case 'class':
        return filteredSubmissions.sort((a, b) => 
          (a.classId?.name || '').localeCompare(b.classId?.name || '')
        );
      case 'score':
        return filteredSubmissions.sort((a, b) => 
          (b.totalScore || 0) - (a.totalScore || 0)
        );
      default:
        return filteredSubmissions;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'pending':
        return 'orange';
      case 'graded':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'graded':
        return 'Graded';
      case 'pending':
        return 'Pending';
      case 'completed':
        return 'Completed';
      default:
        return status || 'Submitted';
    }
  };

  const getScoreColor = (score: number, total: number) => {
    if (!total || total === 0) return 'bg-gray-100 text-gray-800';
    const percentage = (score / total) * 100;
    if (percentage >= 75) return 'bg-green-100 text-green-800';
    if (percentage >= 50) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const calculateTotalScore = (submissions: any[]) => {
    const totalScore = submissions?.reduce((sum, sub) => sum + (sub.score || 0), 0) || 0;
    const totalMarks = submissions?.reduce((sum, sub) => sum + (sub.questionId?.marks || 0), 0) || 0;
    return { totalScore, totalMarks };
  };

  if (loading) {
    return (
      <Box className="p-8 w-full min-h-screen">
        <Box className="text-center">
          <Text size="4" className="text-gray-600">Loading your submissions...</Text>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="p-8 w-full min-h-screen">
        <Box className="text-center">
          <Text size="4" className="text-red-600">{error}</Text>
        </Box>
      </Box>
    );
  }

  const sortedAndFilteredSubmissions = getSortedAndFilteredSubmissions();

  return (
    <Box className="p-8 w-full min-h-screen">
      {/* Title Header Box */}
      <Box className="bg-white rounded-lg p-6 shadow-sm mb-8">
        <Flex justify="center" align="center" className="min-h-[80px]">
          <Heading size="8" className="text-gray-900">
            My Submissions ({submissions.length})
          </Heading>
        </Flex>
      </Box>

      {/* Controls Section */}
      <Flex justify="between" align="center" gap="4" className="mb-6">
        <Flex align="center" gap="4" className="flex-1">
          <Box className="relative w-80">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <TextField.Root
              placeholder="Search by class name..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              className="pl-10 w-full"
              size="3"
            />
          </Box>
          
          <Flex align="center" gap="4">
            <Select.Root value={sortBy} onValueChange={setSortBy}>
              <Select.Trigger className="min-w-[200px]" />
              <Select.Content>
                <Select.Item value="recent">Most Recent</Select.Item>
                <Select.Item value="oldest">Oldest First</Select.Item>
                <Select.Item value="class">Class Name</Select.Item>
                <Select.Item value="score">Score</Select.Item>
              </Select.Content>
            </Select.Root>
            
            <Flex align="center" gap="2">
              <ExclamationTriangleIcon className="w-4 h-4 text-gray-600" />
              <Text size="3" className="text-gray-600">Flagged</Text>
              <Switch
                checked={showFlaggedOnly}
                onCheckedChange={setShowFlaggedOnly}
              />
            </Flex>
          </Flex>
        </Flex>
        
        <Button 
          size="3"
          className="bg-yellow-400 hover:bg-yellow-500 text-black px-6"
          onClick={() => navigate('/my-classes')}
        >
          View My Classes
        </Button>
      </Flex>

      {/* Submissions Table */}
      <Box className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <Box className="grid grid-cols-4 gap-6 p-4 border-b bg-gray-50 font-medium text-sm text-gray-600" style={{ gridTemplateColumns: '2fr 3fr 1fr 1fr' }}>
          <Text>Class</Text>
          <Text>Submitted</Text>
          <Text>Score</Text>
          <Text>Status</Text>
        </Box>

        {submissions.length === 0 ? (
          <Box className="p-12 text-center">
            <Text size="4" className="text-gray-600 mb-6 block">
              You haven't made any submissions yet.
            </Text>
            <Button onClick={() => navigate('/my-classes')}>
              View Available Classes
            </Button>
          </Box>
        ) : sortedAndFilteredSubmissions.length === 0 ? (
          <Box className="p-12 text-center">
            <Text size="4" className="text-gray-600 mb-6 block">
              No submissions match your current filters.
            </Text>
          </Box>
        ) : (
          sortedAndFilteredSubmissions.map((submission) => {
            const isFlagged = submission.submissions?.some(sub => 
              sub.contentModeration?.isInappropriate === true
            ) || false;
            const { totalScore, totalMarks } = calculateTotalScore(submission.submissions || []);

            return (
              <Box
                key={submission.id}
                className="grid grid-cols-4 gap-6 p-4 border-b hover:bg-gray-50 cursor-pointer"
                style={{ gridTemplateColumns: '2fr 3fr 1fr 1fr' }}
                onClick={() => handleViewSubmission(submission)}
              >
                <Flex align="center" gap="2" className="font-medium">
                  <Text>{submission.classId?.name || "Deleted Class"}</Text>
                  {isFlagged && <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />}
                </Flex>
                <Text size="2" className="text-gray-600">
                  {formatDateTime(submission.submittedAt)}
                </Text>
                <Box>
                  <Badge className={getScoreColor(totalScore, totalMarks)}>
                    {submission.status === 'pending' ? 'Pending' : `${totalScore}/${totalMarks}`}
                  </Badge>
                </Box>
                <Box>
                  <Badge color={getStatusColor(submission.status)}>
                    {getStatusLabel(submission.status)}
                  </Badge>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Submission Details Modal */}
      <Dialog.Root open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <Dialog.Content className="max-w-4xl max-h-[90vh] overflow-hidden">
          <Box className="bg-yellow-400 -m-6 mb-0 p-6">
            <Flex justify="between" align="center">
              <Dialog.Title className="text-black text-lg">Submission Details</Dialog.Title>
              {selectedSubmission && (
                <PDFExportButton 
                  submissionData={selectedSubmission as any} 
                  isTeacherView={false}
                  size="2"
                  variant="outline"
                  className="bg-white hover:bg-gray-50 text-black border border-gray-300"
                />
              )}
            </Flex>
          </Box>

          {selectedSubmission && (
            <Box className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <Flex justify="between" align="center" className="mb-6 bg-gray-50 p-4 rounded">
                <Flex direction="column">
                  <Text size="2" className="text-gray-600">Class</Text>
                  <Text weight="medium">{selectedSubmission.classId?.name || "Deleted Class"}</Text>
                </Flex>
                <Flex direction="column">
                  <Text size="2" className="text-gray-600">Status</Text>
                  <Badge color={getStatusColor(selectedSubmission.status)}>
                    {getStatusLabel(selectedSubmission.status)}
                  </Badge>
                </Flex>
                <Flex direction="column">
                  <Text size="2" className="text-gray-600">Submitted</Text>
                  <Text weight="medium">
                    {formatDateTime(selectedSubmission.submittedAt)}
                  </Text>
                </Flex>
              </Flex>

              {selectedSubmission.submissions && selectedSubmission.submissions.length > 0 ? (
                <Tabs.Root defaultValue="questions" className="w-full">
                  <Tabs.List className="grid w-full grid-cols-2 bg-gray-100 p-1 rounded-lg mb-6">
                    <Tabs.Trigger 
                      value="questions" 
                      className="px-4 py-2 text-sm font-medium rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all"
                    >
                      Questions & Answers
                    </Tabs.Trigger>
                    <Tabs.Trigger 
                      value="feedback" 
                      className="px-4 py-2 text-sm font-medium rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all"
                    >
                      Score & Feedback
                    </Tabs.Trigger>
                  </Tabs.List>

                  <Tabs.Content value="questions">
                    <Flex direction="column" gap="6">
                      {selectedSubmission.submissions.map((question, index) => (
                        <Card key={index}>
                          <Box className="p-4 border-b">
                            <Flex align="center" justify="between">
                              <Text size="5" weight="medium">Question {index + 1}</Text>
                              <Badge className={getScoreColor(question.score || 0, question.questionId?.marks || 0)}>
                                Score: {question.score || 0}/{question.questionId?.marks || 0}
                              </Badge>
                            </Flex>
                          </Box>
                          <Box className="p-4 space-y-4">
                            <Box>
                              <Text weight="medium" className="text-gray-900 mb-2 block">Question:</Text>
                              <Box className="bg-gray-50 p-3 rounded">
                                <Text className="text-gray-700">{question.questionId?.question}</Text>
                              </Box>
                            </Box>
                            <Box>
                              <Text weight="medium" className="text-gray-900 mb-2 block">Answer:</Text>
                              <Box className={`p-3 rounded border-l-4 ${
                                question.contentModeration?.isInappropriate 
                                  ? 'bg-red-50 border-red-400' 
                                  : 'bg-blue-50 border-blue-400'
                              }`}>
                                <Text className="text-gray-700 whitespace-pre-wrap">
                                  {question.answer}
                                </Text>
                                {question.contentModeration?.isInappropriate && (
                                  <Box className="mt-3 p-3 bg-red-100 border border-red-300 rounded">
                                    <Flex align="center" gap="2" className="text-red-700 mb-2">
                                      <Text weight="medium">🚩 Content Flagged</Text>
                                    </Flex>
                                    <Text size="2" className="text-red-600">
                                      <strong>Reason:</strong> {question.contentModeration.reason}
                                    </Text>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Card>
                      ))}
                    </Flex>
                  </Tabs.Content>

                  <Tabs.Content value="feedback">
                    <Card>
                      <Box className="p-4 border-b">
                        <Flex align="center" justify="between">
                          <Text size="5" weight="medium">Score Details</Text>
                          <Badge className={getScoreColor(
                            calculateTotalScore(selectedSubmission.submissions).totalScore,
                            calculateTotalScore(selectedSubmission.submissions).totalMarks
                          )}>
                            Total: {calculateTotalScore(selectedSubmission.submissions).totalScore}/
                            {calculateTotalScore(selectedSubmission.submissions).totalMarks}
                          </Badge>
                        </Flex>
                      </Box>
                      <Box className="p-4 space-y-6">
                        <Box>
                          <Text size="2" className="text-gray-600 mb-2 block">Overall Score</Text>
                          <Text size="4" weight="medium">
                            {selectedSubmission.status === 'pending' ? 'Grading pending...' : 
                             `Scored ${calculateTotalScore(selectedSubmission.submissions).totalScore} out of ${calculateTotalScore(selectedSubmission.submissions).totalMarks} marks`}
                          </Text>
                        </Box>

                        <Separator className="my-4" />

                        <Box>
                          <Text weight="medium" className="text-gray-900 mb-3 block">Detailed Feedback:</Text>
                          {selectedSubmission.submissions?.map((question, index) => (
                            <Box key={index} className="mb-6 last:mb-0">
                              <Flex align="center" gap="2" className="mb-2">
                                <Text weight="medium">Question {index + 1}</Text>
                                <Badge className={getScoreColor(question.score || 0, question.questionId?.marks || 0)}>
                                  {question.score || 0}/{question.questionId?.marks || 0}
                                </Badge>
                                {question.attempts > 1 && (
                                  <Text size="2" className="text-gray-500">
                                    (Attempt {question.attempts}/3)
                                  </Text>
                                )}
                              </Flex>
                              <Box className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded">
                                {question.feedback?.aiComment && (
                                  <Box className="mb-3">
                                    <Text weight="medium" className="text-amber-800 block mb-1">AI Feedback:</Text>
                                    <Text className="text-gray-700 leading-relaxed">
                                      {question.feedback.aiComment}
                                    </Text>
                                  </Box>
                                )}
                                {question.feedback?.suggestion && (
                                  <Box>
                                    <Text weight="medium" className="text-amber-800 block mb-1">Suggestion for Improvement:</Text>
                                    <Text className="text-gray-700 leading-relaxed">
                                      {question.feedback.suggestion}
                                    </Text>
                                  </Box>
                                )}
                                {!question.feedback?.aiComment && 
                                 !question.feedback?.suggestion && (
                                  <Text className="text-gray-700 leading-relaxed">
                                    No detailed feedback available for this question.
                                  </Text>
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Card>
                  </Tabs.Content>
                </Tabs.Root>
              ) : (
                <Card className="p-8 text-center">
                  <Text size="4" className="text-gray-600">
                    No detailed submission data available.
                  </Text>
                </Card>
              )}
            </Box>
          )}
          
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="outline">Close</Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Box>
  );
};

export default StudentSubmissions; 